import { BLOCK } from './blocks.js';
import type {
  ArithOp,
  CompareOp,
  Diagnostic,
  Expr,
  LogicOp,
  PrintEnd,
  Program,
  ScalarType,
  ReadKind,
  Stmt,
} from './ir.js';
import { inferTypes } from './types.js';

/** The subset of Blockly's JSON serialization we read. Everything is optional: input is untrusted. */
interface SBlock {
  type?: unknown;
  id?: unknown;
  fields?: Record<string, unknown>;
  inputs?: Record<string, { block?: SBlock; shadow?: SBlock } | undefined>;
  next?: { block?: SBlock; shadow?: SBlock };
  extraState?: unknown;
  enabled?: unknown;
  disabledReasons?: unknown;
}

interface SWorkspace {
  blocks?: { blocks?: SBlock[] };
  variables?: { id?: unknown; name?: unknown }[];
}

const ARITH: Record<string, ArithOp> = { ADD: '+', MINUS: '-', MULTIPLY: '*', DIVIDE: '/' };
const COMPARE: Record<string, CompareOp> = {
  EQ: '==',
  NEQ: '!=',
  LT: '<',
  LTE: '<=',
  GT: '>',
  GTE: '>=',
};
const LOGIC: Record<string, LogicOp> = { AND: 'and', OR: 'or' };
const READ: Record<string, ReadKind> = { INT: 'int', DOUBLE: 'double', TEXT: 'text' };

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/** The `then` dropdown of the inline print block → what follows the value. */
const ELEM_FIELD: Record<string, ScalarType> = {
  INT: 'int',
  DOUBLE: 'double',
  TEXT: 'string',
  BOOL: 'bool',
};

const END_FIELD: Record<string, PrintEnd> = {
  SPACE: 'space',
  NONE: 'none',
  NEWLINE: 'newline',
};

class Parser {
  readonly diagnostics: Diagnostic[] = [];
  private readonly varNames = new Map<string, string>();
  private loopDepth = 0;

  constructor(ws: SWorkspace) {
    for (const v of Array.isArray(ws.variables) ? ws.variables : []) {
      if (typeof v.id === 'string' && typeof v.name === 'string') this.varNames.set(v.id, v.name);
    }
  }

  private idOf(b: SBlock): string {
    return typeof b.id === 'string' ? b.id : '';
  }

  private field(b: SBlock, name: string): unknown {
    return isObject(b.fields) ? b.fields[name] : undefined;
  }

  private varName(b: SBlock): string {
    const f = this.field(b, 'VAR');
    if (isObject(f)) {
      if (typeof f.id === 'string' && this.varNames.has(f.id)) return this.varNames.get(f.id)!;
      if (typeof f.name === 'string') return f.name;
    }
    if (typeof f === 'string') return this.varNames.get(f) ?? f;
    return '?';
  }

  private input(b: SBlock, name: string): SBlock | undefined {
    const slot = isObject(b.inputs) ? b.inputs[name] : undefined;
    return slot?.block ?? slot?.shadow;
  }

  private value(owner: SBlock, name: string, allowRead = false): Expr {
    const child = this.input(owner, name);
    if (!child) {
      this.diagnostics.push({
        code: 'empty-input',
        blockId: this.idOf(owner),
        params: { input: name },
      });
      return { k: 'missing', id: this.idOf(owner) };
    }
    return this.expr(child, allowRead);
  }

  private statements(first: SBlock | undefined): Stmt[] {
    const out: Stmt[] = [];
    for (let b = first; b; b = b.next?.block) {
      const disabled =
        b.enabled === false || (Array.isArray(b.disabledReasons) && b.disabledReasons.length > 0);
      if (disabled) continue;
      const s = this.stmt(b);
      if (s) out.push(s);
    }
    return out;
  }

  private loopBody(b: SBlock): Stmt[] {
    this.loopDepth++;
    try {
      return this.statements(this.input(b, 'DO'));
    } finally {
      this.loopDepth--;
    }
  }

  private stmt(b: SBlock): Stmt | null {
    const id = this.idOf(b);
    switch (b.type) {
      case 'variables_set':
        return { k: 'set', id, name: this.varName(b), value: this.value(b, 'VALUE', true) };
      case 'math_change':
        return { k: 'change', id, name: this.varName(b), delta: this.value(b, 'DELTA') };
      case BLOCK.newList:
      case BLOCK.newTable: {
        const elem = ELEM_FIELD[String(this.field(b, 'ELEM'))] ?? 'int';
        const base = this.field(b, 'BASE') === 'ONE' ? 1 : 0;
        const dims: [Expr] | [Expr, Expr] =
          b.type === BLOCK.newTable
            ? [this.value(b, 'ROWS'), this.value(b, 'COLS')]
            : [this.value(b, 'SIZE')];
        return { k: 'newArray', id, name: this.varName(b), elem, dims, base };
      }
      case BLOCK.setAt:
      case BLOCK.setAtTable:
        return {
          k: 'setAt',
          id,
          name: this.varName(b),
          index: this.value(b, 'INDEX'),
          index2: b.type === BLOCK.setAtTable ? this.value(b, 'INDEX2') : null,
          value: this.value(b, 'VALUE', true),
        };
      case BLOCK.print:
        return { k: 'print', id, value: this.value(b, 'VALUE'), end: 'newline' };
      case BLOCK.printInline:
        return {
          k: 'print',
          id,
          value: this.value(b, 'VALUE'),
          end: END_FIELD[String(this.field(b, 'END'))] ?? 'space',
        };
      case 'controls_if': {
        const state = isObject(b.extraState) ? b.extraState : {};
        const elseIfCount = typeof state.elseIfCount === 'number' ? state.elseIfCount : 0;
        const branches = [];
        for (let i = 0; i <= elseIfCount; i++) {
          branches.push({
            cond: this.value(b, `IF${i}`),
            body: this.statements(this.input(b, `DO${i}`)),
          });
        }
        const orElse = state.hasElse === true ? this.statements(this.input(b, 'ELSE')) : null;
        return { k: 'if', id, branches, orElse };
      }
      case BLOCK.repeat: {
        const times = this.value(b, 'TIMES');
        return { k: 'repeat', id, times, body: this.loopBody(b) };
      }
      case BLOCK.while: {
        const cond = this.value(b, 'COND');
        return { k: 'while', id, cond, body: this.loopBody(b) };
      }
      case BLOCK.for: {
        const from = this.value(b, 'FROM');
        const to = this.value(b, 'TO');
        return {
          k: 'for',
          id,
          name: this.varName(b),
          from,
          to,
          down: this.field(b, 'DIR') === 'DOWN',
          body: this.loopBody(b),
        };
      }
      case BLOCK.break:
        if (this.loopDepth === 0)
          this.diagnostics.push({ code: 'break-outside-loop', blockId: id });
        return { k: 'break', id };
      default:
        this.diagnostics.push({
          code: 'unknown-block',
          blockId: id,
          params: { type: String(b.type) },
        });
        return null;
    }
  }

  private expr(b: SBlock, allowRead = false): Expr {
    const id = this.idOf(b);
    switch (b.type) {
      case 'math_number': {
        const raw = this.field(b, 'NUM');
        const value = typeof raw === 'number' ? raw : Number(raw);
        return { k: 'num', id, value: Number.isFinite(value) ? value : 0 };
      }
      case 'text': {
        const raw = this.field(b, 'TEXT');
        return { k: 'text', id, value: typeof raw === 'string' ? raw : '' };
      }
      case 'logic_boolean':
        return { k: 'bool', id, value: this.field(b, 'BOOL') !== 'FALSE' };
      case 'variables_get':
        return { k: 'var', id, name: this.varName(b) };
      case BLOCK.read:
        if (!allowRead) this.diagnostics.push({ code: 'read-placement', blockId: id });
        return { k: 'read', id, as: READ[String(this.field(b, 'AS'))] ?? 'int' };
      case BLOCK.arith:
        return {
          k: 'arith',
          id,
          op: ARITH[String(this.field(b, 'OP'))] ?? '+',
          left: this.value(b, 'A'),
          right: this.value(b, 'B'),
        };
      case BLOCK.intDiv:
        return { k: 'arith', id, op: 'idiv', left: this.value(b, 'A'), right: this.value(b, 'B') };
      case BLOCK.mod:
        return { k: 'arith', id, op: 'mod', left: this.value(b, 'A'), right: this.value(b, 'B') };
      case 'logic_compare':
        return {
          k: 'compare',
          id,
          op: COMPARE[String(this.field(b, 'OP'))] ?? '==',
          left: this.value(b, 'A'),
          right: this.value(b, 'B'),
        };
      case 'logic_operation':
        return {
          k: 'logic',
          id,
          op: LOGIC[String(this.field(b, 'OP'))] ?? 'and',
          left: this.value(b, 'A'),
          right: this.value(b, 'B'),
        };
      case 'logic_negate':
        return { k: 'not', id, arg: this.value(b, 'BOOL') };
      case BLOCK.at:
      case BLOCK.atTable:
        return {
          k: 'at',
          id,
          name: this.varName(b),
          index: this.value(b, 'INDEX'),
          index2: b.type === BLOCK.atTable ? this.value(b, 'INDEX2') : null,
        };
      case BLOCK.size:
        return {
          k: 'size',
          id,
          name: this.varName(b),
          dim: this.field(b, 'DIM') === 'TWO' ? 2 : 1,
        };
      case BLOCK.join:
        return { k: 'join', id, left: this.value(b, 'A'), right: this.value(b, 'B') };
      case 'text_length':
        return { k: 'length', id, arg: this.value(b, 'VALUE') };
      case BLOCK.charAt:
        // The same IR as a list place: the variable's type says it is a character of a text.
        return { k: 'at', id, name: this.varName(b), index: this.value(b, 'INDEX'), index2: null };
      case BLOCK.code:
        return { k: 'code', id, arg: this.value(b, 'VALUE') };
      case BLOCK.fromCode:
        return { k: 'chr', id, arg: this.value(b, 'VALUE') };
      case BLOCK.changeCase:
        return {
          k: 'case',
          id,
          upper: this.field(b, 'CASE') !== 'LOWER',
          arg: this.value(b, 'VALUE'),
        };
      case BLOCK.find:
        return { k: 'find', id, needle: this.value(b, 'NEEDLE'), hay: this.value(b, 'TEXT') };
      case BLOCK.substr:
        return {
          k: 'substr',
          id,
          arg: this.value(b, 'TEXT'),
          from: this.value(b, 'FROM'),
          len: this.value(b, 'LEN'),
        };
      default:
        this.diagnostics.push({
          code: 'unknown-block',
          blockId: id,
          params: { type: String(b.type) },
        });
        return { k: 'missing', id };
    }
  }

  parse(ws: SWorkspace): Program {
    const top = Array.isArray(ws.blocks?.blocks) ? ws.blocks.blocks : [];
    const starts = top.filter((b) => isObject(b) && b.type === BLOCK.start);
    const [start, ...extra] = starts;
    for (const b of extra) this.diagnostics.push({ code: 'extra-start', blockId: this.idOf(b) });
    if (!start) this.diagnostics.push({ code: 'no-start', blockId: null });
    const body = start ? this.statements(start.next?.block) : [];
    const program: Program = {
      startId: start ? this.idOf(start) : null,
      body,
      variables: [],
      types: {},
      diagnostics: this.diagnostics,
    };
    return inferTypes(program);
  }
}

/**
 * Build the program IR from Blockly's workspace JSON. Only the stack under the first
 * "when program starts" block is the program; other top-level stacks are ignored (the editor
 * shows them disabled). Never throws on malformed input: problems become diagnostics.
 */
export function parseWorkspace(json: unknown): Program {
  const ws: SWorkspace = isObject(json) ? json : {};
  return new Parser(ws).parse(ws);
}
